/**
 * SkillTreeMap — 产业链地图主组件（v4）
 *
 * v4 核心变化：
 *   - Tier 分级：节点大小代表战略重要性
 *   - 线条降级：默认细灰线，hover/选中时彩色高亮
 *   - 节点主导：视觉重量从线转移到卡片
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState,
  type Node, type Edge, type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';

/**
 * 高亮状态 Context — 让节点/边组件自己读取 dimmed/highlighted/selected，
 * 避免 hover / 搜索 / 筛选 / 选中时重建整个 nodes/edges 数组（消除闪烁根因）
 */
import { HighlightContext } from './HighlightContext';

import type { ChainNode, ChainEdge, Company, FinancialRecord, Ranking } from '../../types';
import {
  computeSkillTreeLayout, identifyMainTrunk, isCoreNode, computeTier,
  computePathLayout,
  type LaidOutNode, COLUMN_WIDTH, LAYER_HEADER_Y, TIER_SIZES,
} from '../../utils/skilltree-layout';
import { TYPE_LABELS } from '../../utils/chain-constants';
import { isTollStation } from '../../utils/chain-helpers';

import SkillTreeNode, { type SkillTreeNodeData } from './SkillTreeNode';
import SkillTreeEdge from './SkillTreeEdge';
import SkillTreeDefs from './SkillTreeDefs';
import ModeSwitcher, { type SkillTreeMode } from './ModeSwitcher';

/** 层级表头节点 — 支持普通标签和面包屑 */
interface BreadcrumbItem {
  text: string;
  color: string;
  isFocus: boolean;
}

interface LayerHeaderData {
  label: string | BreadcrumbItem[];
  color: string;
  isBreadcrumb?: boolean;
}

function LayerHeaderNode({ data }: { data: LayerHeaderData }) {
  const isBreadcrumb = data.isBreadcrumb;
  const isArray = Array.isArray(data.label);

  if (isBreadcrumb && isArray) {
    const items = data.label as BreadcrumbItem[];
    // 面包屑渲染
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        pointerEvents: 'none',
        userSelect: 'none',
        flexWrap: 'wrap',
        maxWidth: 800,
      }}>
        {items.map((item, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <span style={{ color: '#475569' }}>→</span>}
            <span style={{
              color: item.color,
              fontWeight: item.isFocus ? 700 : 400,
              fontSize: item.isFocus ? 14 : 12,
            }}>
              {item.text}
            </span>
          </span>
        ))}
      </div>
    );
  }

  // 普通标签渲染
  const label = isArray ? (data.label as BreadcrumbItem[])[0]?.text ?? '' : data.label as string;
  return (
    <div style={{
      fontSize: 13,
      fontWeight: 700,
      color: data.color,
      textAlign: 'center',
      width: TIER_SIZES[1].width,
      textShadow: '0 0 8px rgba(0,0,0,0.6)',
      pointerEvents: 'none',
      userSelect: 'none',
      letterSpacing: 0.5,
    }}>
      {label}
    </div>
  );
}

const nodeTypes = { skillTree: SkillTreeNode, layerHeader: LayerHeaderNode };
const edgeTypes = { skillTree: SkillTreeEdge };

/** 视图模式：global=全局横向图, path=纵向路径图 */
export type SkillTreeViewMode = 'global' | 'path';

/** 层级显示标签（按 displayCol 顺序：0=矿物 → 8=系统） */
const LAYER_DISPLAY_LABELS: Record<number, string> = {
  8: '矿物/矿产',
  7: '化学品',
  6: '材料',
  5: '零部件',
  4: '设备',
  3: '制程/封装',
  2: '芯片',
  1: '模组',
  0: '系统/终端',
};

export interface SkillTreeMapProps {
  allNodes: ChainNode[];
  allEdges: ChainEdge[];
  chain: string;
  onSelectNode: (id: string | null) => void;
  selectedNode: string | null;
  selectedCompanyId: string | null;
  financials: FinancialRecord[];
  rankings: Ranking[];
  companies: Company[];
}

export default function SkillTreeMap({
  allNodes, allEdges, chain, onSelectNode, selectedNode, selectedCompanyId,
  companies, rankings,
}: SkillTreeMapProps) {
  const [mode, setMode] = useState<SkillTreeMode>('overview');
  const [viewMode, setViewMode] = useState<SkillTreeViewMode>('global');
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<Node>([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [instance, setInstance] = useState<ReactFlowInstance | null>(null);
  // 进入 path mode 前的 viewport（{x, y, zoom}），返回 global 时还原而非 fitView
  const [prePathViewport, setPrePathViewport] = useState<{ x: number; y: number; zoom: number } | null>(null);
  // viewport 已主动还原，通知 fitView useEffect 跳过下一次 fitView（避免覆盖）
  // 用 ref 不用 state：effect 内读/写 ref 不违反 react-hooks/set-state-in-effect
  const skipNextFitViewRef = useRef(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [bottleneckFilter, setBottleneckFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [investableOnly, setInvestableOnly] = useState(false);

  // 当选中节点时，自动切换到 path mode — React 19 渲染期同步模式
  // 进入 path 前的 viewport 在 onNodeClick 事件里保存（事件回调非渲染期，符合 lint 规则）
  const [prevSelectedNode, setPrevSelectedNode] = useState(selectedNode ?? null);
  if (selectedNode && selectedNode !== prevSelectedNode) {
    setPrevSelectedNode(selectedNode);
    setViewMode('path');
  }
  if (!selectedNode && prevSelectedNode) {
    setPrevSelectedNode(null);
    if (viewMode === 'path') {
      setViewMode('global');
    }
  }

  // 1) 按 chain 过滤
  const chainFilteredNodes = useMemo(() => {
    if (chain === 'all') return allNodes;
    return allNodes.filter(n => {
      const ch = n.chains ?? [];
      if (ch.length === 0) return true;
      return ch.includes(chain);
    });
  }, [chain, allNodes]);

  // 2) 按 mode 过滤
  const filteredNodes = useMemo(() => {
    if (mode === 'all') return chainFilteredNodes;
    return chainFilteredNodes.filter(n => isCoreNode(n));
  }, [chainFilteredNodes, mode]);

  // 3) 链路角色
  const nodeChainRole = useMemo(() => {
    const map = new Map<string, 'lead' | 'shared' | 'weak' | 'global'>();
    for (const n of allNodes) {
      const ch = n.chains ?? [];
      if (ch.length === 0) { map.set(n.id, 'global'); continue; }
      if (chain === 'all') { map.set(n.id, ch.length >= 2 ? 'shared' : 'lead'); continue; }
      if (!ch.includes(chain)) { map.set(n.id, 'weak'); continue; }
      map.set(n.id, ch.length === 1 ? 'lead' : 'shared');
    }
    return map;
  }, [chain, allNodes]);

  const filteredEdges = useMemo(() => {
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    return allEdges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
  }, [allEdges, filteredNodes]);

  // 3.5) 路径布局（path mode）
  const pathLayout = useMemo(() => {
    if (viewMode !== 'path' || !selectedNode) return null;
    return computePathLayout(allNodes, allEdges, selectedNode, {
      width: viewMode === 'path' ? 280 : TIER_SIZES[1].width,
      height: viewMode === 'path' ? 56 : TIER_SIZES[1].height,
    });
  }, [viewMode, selectedNode, allNodes, allEdges]);

  // 4) 投资吸引力 + Top公司 + Tier 计算
  const nodeInvestData = useMemo(() => {
    const ipsMap = new Map<string, number>();
    for (const r of rankings) {
      if (r.ips_score != null) ipsMap.set(r.company_id, r.ips_score);
    }
    for (const c of companies) {
      if (c.ips_score != null && !ipsMap.has(c.company_id)) {
        ipsMap.set(c.company_id, c.ips_score);
      }
    }
    const investableMap = new Map<string, boolean>();
    for (const c of companies) {
      investableMap.set(c.company_id, c.cn_access_level === 'direct' || c.cn_access_level === 'indirect');
    }
    const companyMap = new Map(companies.map(c => [c.company_id, c]));

    const result = new Map<string, {
      avgIps: number;
      hasInvestable: boolean;
      attractiveness: 'high' | 'medium' | 'low';
      topCompanies: string[];
      tier: 1 | 2 | 3;
    }>();

    for (const n of allNodes) {
      const comps = n.companies ?? [];
      const ipsValues = comps.map(id => ipsMap.get(id)).filter((v): v is number => v != null);
      const avgIps = ipsValues.length > 0
        ? ipsValues.reduce((a, b) => a + b, 0) / ipsValues.length
        : 0;
      const hasInvestable = comps.some(id => investableMap.get(id));
      const toll = isTollStation(n);
      const attractiveness: 'high' | 'medium' | 'low' =
        toll || avgIps >= 70 ? 'high' : avgIps >= 50 ? 'medium' : 'low';
      const tier = computeTier(n, avgIps, toll, hasInvestable);

      const sortedComps = comps
        .map(id => ({ company: companyMap.get(id), ips: ipsMap.get(id) ?? 0 }))
        .filter(x => x.company)
        .sort((a, b) => b.ips - a.ips);
      const topCompanies = sortedComps.slice(0, 2).map(x => x.company!.company);

      result.set(n.id, { avgIps, hasInvestable, attractiveness, topCompanies, tier });
    }
    return result;
  }, [allNodes, companies, rankings]);

  // 5) Tier Map（供布局使用）
  const tierMap = useMemo(() => {
    const m = new Map<string, 1 | 2 | 3>();
    nodeInvestData.forEach((v, k) => m.set(k, v.tier));
    return m;
  }, [nodeInvestData]);

  // 6) 布局
  const layout = useMemo(
    () => computeSkillTreeLayout(filteredNodes, filteredEdges, tierMap),
    [filteredNodes, filteredEdges, tierMap]
  );
  const trunk = useMemo(() => identifyMainTrunk(filteredNodes, filteredEdges), [filteredNodes, filteredEdges]);

  // 7) 邻域高亮
  const neighborhoodIds = useMemo(() => {
    const focus = hoveredNodeId ?? selectedNode;
    if (!focus) return null;
    const result = new Set<string>([focus]);
    let frontier = [focus];
    for (let depth = 0; depth < 2; depth++) {
      const next: string[] = [];
      for (const id of frontier) {
        for (const e of filteredEdges) {
          if (e.source === id && !result.has(e.target)) { result.add(e.target); next.push(e.target); }
          if (e.target === id && !result.has(e.source)) { result.add(e.source); next.push(e.source); }
        }
      }
      frontier = next;
    }
    return result;
  }, [hoveredNodeId, selectedNode, filteredEdges]);

  // 7b) 公司选中时，派生关联节点集合（用于三视图联动高亮）
  const companyRelatedIds = useMemo(() => {
    if (!selectedCompanyId) return null;
    const ids = new Set<string>();
    for (const n of allNodes) {
      if (n.companies?.includes(selectedCompanyId)) ids.add(n.id);
    }
    return ids.size > 0 ? ids : null;
  }, [selectedCompanyId, allNodes]);

  // 8) 搜索匹配
  const searchMatchIds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const ids = new Set<string>();
    for (const n of filteredNodes) {
      if (n.name.toLowerCase().includes(q)) ids.add(n.id);
    }
    for (const c of companies) {
      if (c.company.toLowerCase().includes(q) || c.ticker?.toLowerCase().includes(q)) {
        for (const nodeId of c.nodes ?? []) {
          if (filteredNodes.some(n => n.id === nodeId)) ids.add(nodeId);
        }
      }
    }
    return ids;
  }, [searchQuery, filteredNodes, companies]);

  // 9) 筛选匹配
  const filterMatchIds = useMemo(() => {
    if (bottleneckFilter === 'all' && typeFilter === 'all' && !investableOnly) return null;
    const ids = new Set<string>();
    for (const n of filteredNodes) {
      if (bottleneckFilter !== 'all' && n.bottleneck_level !== bottleneckFilter) continue;
      if (typeFilter !== 'all' && n.type !== typeFilter) continue;
      if (investableOnly && !nodeInvestData.get(n.id)?.hasInvestable) continue;
      ids.add(n.id);
    }
    return ids;
  }, [bottleneckFilter, typeFilter, investableOnly, filteredNodes, nodeInvestData]);

  // 10) 装配 rfNodes — 支持 global 和 path 两种模式
  useEffect(() => {
    // PATH MODE：纵向路径布局
    if (viewMode === 'path' && pathLayout) {
      const newNodes: Node[] = [];
      const nodeMap = new Map(allNodes.map(n => [n.id, n]));

      // 添加面包屑
      const pathNodeIds = Array.from(pathLayout.nodes.keys());
      const focusIndex = pathNodeIds.indexOf(selectedNode!);
      newNodes.push({
        id: 'breadcrumb',
        type: 'layerHeader',
        position: { x: 0, y: LAYER_HEADER_Y },
        data: {
          label: pathNodeIds.map((id, i) => {
            const node = nodeMap.get(id);
            const isFocus = i === focusIndex;
            const color = isFocus ? '#10b981' : '#94a3b8';
            return { text: node?.name ?? id, color, isFocus };
          }),
          isBreadcrumb: true,
          color: '#94a3b8',
        },
        draggable: false,
        selectable: false,
        connectable: false,
      });

      // 添加路径节点
      for (const [nodeId, pos] of pathLayout.nodes) {
        const n = nodeMap.get(nodeId);
        if (!n) continue;
        const inv = nodeInvestData.get(nodeId);
        const role = nodeChainRole.get(nodeId) ?? 'global';
        const isFocus = nodeId === selectedNode;

        newNodes.push({
          id: nodeId,
          type: 'skillTree',
          position: { x: pos.x + 80, y: pos.y + 50 }, // 居中显示
          data: {
            label: n.name,
            nodeType: n.type,
            width: pos.width,
            height: pos.height,
            tier: inv?.tier ?? 2,
            bottleneckLevel: n.bottleneck_level,
            bottleneckReason: n.bottleneck_reason,
            chains: n.chains ?? [],
            isToll: isTollStation(n),
            isScarce: n.bottleneck_level === 'high' && !isTollStation(n),
            isIrreplaceable: (n.companies?.length ?? 0) <= 2,
            companyCount: n.companies?.length ?? 0,
            investAttractiveness: inv?.attractiveness ?? 'low',
            hasInvestable: inv?.hasInvestable ?? false,
            avgIps: inv?.avgIps ?? 0,
            topCompanies: inv?.topCompanies ?? [],
            chainRole: role,
            isFocus,
          } as SkillTreeNodeData,
          draggable: false,
        });
      }

      setRfNodes(newNodes);
      return;
    }

    // GLOBAL MODE：横向 DAG 布局
    const nodeMap = new Map<string, LaidOutNode>();
    layout.forEach(v => nodeMap.set(v.id, v));

    const newNodes: Node[] = [];

    // 层级表头节点
    const maxDisplayCol = Math.max(0, ...Array.from(layout.values()).map(v => v.displayCol));
    for (let col = 0; col <= maxDisplayCol; col++) {
      const layer = maxDisplayCol - col;
      const label = LAYER_DISPLAY_LABELS[layer] || `L${layer}`;
      newNodes.push({
        id: `header-col-${col}`,
        type: 'layerHeader',
        position: { x: col * COLUMN_WIDTH, y: LAYER_HEADER_Y },
        data: { label, color: '#94a3b8' },
        draggable: false,
        selectable: false,
        connectable: false,
      });
    }

    // 数据节点
    for (const n of filteredNodes) {
      const pos = nodeMap.get(n.id);
      if (!pos) continue;
      const inv = nodeInvestData.get(n.id);
      const role = nodeChainRole.get(n.id) ?? 'global';

      newNodes.push({
        id: n.id,
        type: 'skillTree',
        position: { x: pos.x, y: pos.y },
        data: {
          label: n.name,
          nodeType: n.type,
          width: pos.width,
          height: pos.height,
          tier: pos.tier,
          bottleneckLevel: n.bottleneck_level,
          bottleneckReason: n.bottleneck_reason,
          chains: n.chains ?? [],
          isToll: isTollStation(n),
          isScarce: n.bottleneck_level === 'high' && !isTollStation(n),
          isIrreplaceable: (n.companies?.length ?? 0) <= 2,
          companyCount: n.companies?.length ?? 0,
          investAttractiveness: inv?.attractiveness ?? 'low',
          hasInvestable: inv?.hasInvestable ?? false,
          avgIps: inv?.avgIps ?? 0,
          topCompanies: inv?.topCompanies ?? [],
          chainRole: role,
        } as SkillTreeNodeData,
        draggable: false,
      });
    }

    setRfNodes(newNodes);
    // selectedNode 不在依赖里：global mode 不用它；path mode 通过 pathLayout 传递（pathLayout 依赖 selectedNode）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, pathLayout, layout, filteredNodes, nodeChainRole, nodeInvestData, allNodes, setRfNodes]);

  // 11) 装配 rfEdges — 支持 global 和 path 两种模式
  useEffect(() => {
    // PATH MODE：纵向路径边，带标签
    if (viewMode === 'path' && pathLayout) {
      const newEdges: Edge[] = pathLayout.pathEdges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: 'skillTree',
        data: {
          chains: e.chains ?? [],
          isMainTrunk: false,
          isSameLayer: false,
          confidence: e.confidence ?? 'medium',
          relation: e.relation,
          showLabel: true, // path mode 下显示标签
        } as Record<string, unknown>,
      }));
      setRfEdges(newEdges);
      return;
    }

    // GLOBAL MODE：横向 DAG 边
    const newEdges: Edge[] = filteredEdges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'skillTree',
      data: {
        chains: e.chains ?? [],
        isMainTrunk: trunk.has(e.id),
        isSameLayer: false,
        confidence: e.confidence ?? 'medium',
        showLabel: false,
      } as Record<string, unknown>,
    }));
    setRfEdges(newEdges);
  }, [viewMode, pathLayout, filteredEdges, trunk, setRfEdges]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (node.type === 'layerHeader') return;
    if (selectedNode === node.id) {
      // 再次点击同一节点 → 取消选择（onPaneClick 也会还原 viewport）
      onSelectNode(null);
    } else {
      // 进入 path mode：保存当前 viewport 到 state（事件回调非渲染期，符合 lint 规则）
      if (instance && viewMode === 'global') {
        setPrePathViewport(instance.getViewport());
      }
      onSelectNode(node.id);
    }
  }, [onSelectNode, selectedNode, instance, viewMode]);
  const onNodeMouseEnter = useCallback((_: React.MouseEvent, node: Node) => {
    if (node.type === 'layerHeader') return;
    setHoveredNodeId(node.id);
  }, []);
  const onNodeMouseLeave = useCallback(() => setHoveredNodeId(null), []);
  const onPaneClick = useCallback(() => {
    // 在 path mode 下点击空白区域返回 global mode
    if (viewMode === 'path') {
      setViewMode('global');
      // 还原进入 path 前的 viewport（用户缩放/平移的位置），而非 fitView
      if (instance && prePathViewport) {
        instance.setViewport(prePathViewport, { duration: 300 });
        setPrePathViewport(null);
        skipNextFitViewRef.current = true;
      }
    }
    onSelectNode(null);
  }, [viewMode, instance, prePathViewport, onSelectNode]);
  const onInit = useCallback((inst: ReactFlowInstance) => setInstance(inst), []);

  // Esc / 其他清空路径触发：selectedNode 从有变无 + viewMode='path' → 还原 viewport
  // 同步设置 skipNextFitViewRef=true，第二个 useEffect 跳过本次 fitView
  // 消费后立即清零 prePathViewport，避免陈旧缓存被未来 instance 变化误读
  useEffect(() => {
    if (!instance) return;
    if (!selectedNode && viewMode === 'global' && prePathViewport) {
      instance.setViewport(prePathViewport, { duration: 300 });
      skipNextFitViewRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- 收敛条件清晰：消费后即清零
      setPrePathViewport(null);
    }
  }, [selectedNode, viewMode, prePathViewport, instance]);

  useEffect(() => {
    if (!instance) return;
    // viewport 已通过 onPaneClick / Esc 还原，本次跳过 fitView
    if (skipNextFitViewRef.current) {
      skipNextFitViewRef.current = false;
      return;
    }
    // global mode 或 path mode 都触发 fitView
    if (layout.size || pathLayout) {
      const t = setTimeout(() => instance.fitView({ padding: 0.12, duration: 300 }), 100);
      return () => clearTimeout(t);
    }
  }, [instance, layout.size, pathLayout, chain, mode, viewMode]);

  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    for (const n of filteredNodes) types.add(n.type);
    return Array.from(types).sort();
  }, [filteredNodes]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#0b1220' }}>
      <SkillTreeDefs />

      {/* 搜索 & 筛选栏 — 移到 ModeSwitcher 下方避免遮挡 */}
      <div style={{
        position: 'absolute', top: 52, left: 8, right: 8, zIndex: 10,
        display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
        pointerEvents: 'auto',
      }}>
        <input
          type="text"
          placeholder="搜索 公司/ticker/节点…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 200px', minWidth: 150, maxWidth: 280,
            padding: '5px 10px', borderRadius: 6,
            background: 'rgba(15,23,42,0.9)', color: '#e2e8f0',
            border: '1px solid #334155', fontSize: 12,
            outline: 'none',
          }}
        />
        <select
          value={bottleneckFilter}
          onChange={e => setBottleneckFilter(e.target.value as 'all' | 'high' | 'medium' | 'low')}
          style={filterSelectStyle}
        >
          <option value="all">全部瓶颈</option>
          <option value="high">高瓶颈</option>
          <option value="medium">中瓶颈</option>
          <option value="low">低瓶颈</option>
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="all">全部类型</option>
          {availableTypes.map(t => (
            <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
          ))}
        </select>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
          padding: '4px 8px', borderRadius: 6,
          background: investableOnly ? 'rgba(34,197,94,0.2)' : 'rgba(15,23,42,0.9)',
          border: `1px solid ${investableOnly ? '#22c55e' : '#334155'}`,
          fontSize: 11, color: investableOnly ? '#22c55e' : '#94a3b8',
          whiteSpace: 'nowrap',
        }}>
          <input
            type="checkbox"
            checked={investableOnly}
            onChange={e => setInvestableOnly(e.target.checked)}
            style={{ display: 'none' }}
          />
          ● 可投资
        </label>
      </div>

      <HighlightContext.Provider value={{
        neighborhoodIds,
        searchMatchIds,
        filterMatchIds,
        selectedNodeId: selectedNode,
        companyRelatedIds,
      }}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onPaneClick={onPaneClick}
        onInit={onInit}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        minZoom={0.1}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
      >
        <Background color="#1e293b" gap={24} />
        <Controls
          showInteractive={false}
          style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid #334155' }}
        />
        <MiniMap
          nodeColor={(n) => {
            const data = n.data as SkillTreeNodeData;
            if (!data?.investAttractiveness) return '#1e293b';
            if (data.investAttractiveness === 'high') return '#fbbf24';
            if (data.investAttractiveness === 'medium') return '#3b82f6';
            return '#1e293b';
          }}
          maskColor="rgba(11,18,32,0.6)"
          style={{ background: '#0f172a', border: '1px solid #334155' }}
        />
      </ReactFlow>
      </HighlightContext.Provider>

      <ModeSwitcher mode={mode} onChange={setMode} />
    </div>
  );
}

const filterSelectStyle: React.CSSProperties = {
  padding: '5px 8px', borderRadius: 6,
  background: 'rgba(15,23,42,0.9)', color: '#e2e8f0',
  border: '1px solid #334155', fontSize: 12,
  outline: 'none', cursor: 'pointer',
};
